import { create } from 'zustand';
import { CompetitorInterface } from 'Services/Query/Competitors/Types';
import { Product } from '@setvi/shared/interfaces';

import { RFQProducts } from '../interfaces';

export interface AnalyticsProps {
  time: string;
  processed: number;
  matched: number;
  notMatched: number;
  onlineMatched: number;
  mfgMatched: number;
  nonMfgMatched: number;
  fileProcessingTime: number;
  exactMatch?: number;
  possibleMatch?: number;
  closeMatch?: number;
  privateLabelMatched?: number;
  privateLabelSwapped?: number;
  markedAsNonMatched?: number;
  markedAsNonMatch?: number;
  removedFromQuotes?: number;
  userSelection?: number;
}

interface RankingValues {
  enablePrivateLabelRanking: boolean;
  enableStockProductRanking: boolean;
  enableVendorRanking: boolean;
}

interface AdditionalParams {
  enablePostProcessing?: boolean;
  enableMatchProductBySku?: boolean;
  enableFindProductOnline?: boolean;
  enableGettingCompetitorProductDescription?: boolean;
}

export interface ProcessingTotals {
  all: number;
  matched: number;
  notMatched: number;
  mfgSkuMatched: number;
  foundOnline: number;
  nonMfgSkuMatched: number;
  exactMatch: number;
  possibleMatch: number;
  closeMatch: number;
  privateLabelMatched?: number;
  privateLabelSwapped?: number;
  markedAsNonMatched?: number;
  removedFromQuote?: number;
  userSelection?: number;
}

export interface FileInfo {
  id: string;
  analytics?: ProcessingTotals;
  statusId?: string;
  status:
    | 'UPLOADING'
    | 'AWAITING_PROCESSING'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'PENDING'
    | 'FAILED';
  file: {
    url: string | null;
    name: string;
    size?: number;
    type?: string;
    total?: number;
  };
}

export interface RFQStoreStates {
  // RFQ Details
  description: string;
  name: string;
  competitor: CompetitorInterface;

  // quote related details
  quoteId?: string;
  // customer related details
  customerId?: string;
  locationId?: string;
  // prospect related details
  segmentId?: string;
  prospect?: {
    name?: string;
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
  };

  // Products & Processing
  products: RFQProducts[];
  processedProducts: RFQProducts[];
  selectedProducts: Product[];
  productDetails: Product | null;

  // File Management
  files: FileInfo[];
  currentFileId: string | null;
  selectedFile: string | null;
  currentJobId: string | null;

  // Processing State
  uploading: boolean;
  processing: boolean;
  polling: boolean;
  completed: boolean;
  isDialogOpen: boolean;
  isSocketActive: boolean;
  isSorting: boolean;

  // Analytics & Totals
  analytics: AnalyticsProps;
  totalsToUpdate: Partial<ProcessingTotals>;
  processingTime: string;
  privateLabelsSelected: string[];

  // Configuration
  rankings: RankingValues;
  params: AdditionalParams;

  // We will use this to filter analytics instead of files
  filterAnalytics?: AnalyticsProps;
}

interface Store extends RFQStoreStates {
  // Core Updates
  updateCompetitor: (newCompetitor: CompetitorInterface) => void;
  updateAdditionalParams: (params: AdditionalParams) => void;
  updateAnalytics: (newAnalytics: AnalyticsProps) => void;
  updateAnalytic: (newAnalytics: Partial<AnalyticsProps>) => void;
  updateProducts: (newProducts: RFQProducts[]) => void;
  setProducts: (newProducts: RFQProducts[]) => void;
  addProducts: (newProducts: RFQProducts[]) => void;
  updateProcessedProducts: (newProducts: RFQProducts[]) => void;
  appendProcessedProducts: (newProcessedProducts: RFQProducts) => void;
  updateProcessedProduct: (
    id: string,
    updatedProduct: Partial<RFQProducts>
  ) => void;
  updateSelectedProducts: (products: Product[]) => void;
  updateProductDetails: (product: Product | null) => void;
  updateRankings: (newRankings: RankingValues) => void;
  updateDescription: (newDescription: string) => void;
  updateCompleted: (newCompleted: boolean) => void;
  updateProcessing: (newProcessing: boolean) => void;
  updatePolling: (newPolling: boolean) => void;
  updateName: (newName: string) => void;
  updateTotalsToUpdate: (totals: Partial<ProcessingTotals>) => void;
  updateProcessingTime: (time: string) => void;
  updatePrivateLabelsSelected: (ids: string[]) => void;
  updateSorting: (newSorting: boolean) => void;

  updateQuoteId: (quoteId: string) => void;
  updateCustomerId: (customerId: string) => void;
  updateLocationId: (locationId: string) => void;
  updateSegmentId: (segmentId: string) => void;
  updateProspect: (prospect: {
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
    name?: string;
  }) => void;

  // File Management
  updateFileUrls: (files: FileInfo[]) => void;
  addFileUrl: (file: FileInfo) => void;
  removeFileUrl: (index: number) => void;
  updateFile: (id: string, file: Partial<FileInfo>) => void;
  updateSelectedFile: (fileName: string | null) => void;

  // Dialog State
  updateDialogOpen: (isOpen: boolean) => void;

  // Batch Update
  batchUpdate: (updates: Partial<RFQStoreStates>) => void;

  updateFilterAnalytics: (
    analytics: Partial<AnalyticsProps> | undefined
  ) => void;

  reset: () => void;
}

const initAnalytics = {
  time: '',
  fileProcessingTime: 0,
  notMatched: 0,
  onlineMatched: 0,
  mfgMatched: 0,
  matched: 0,
  processed: 0,
  nonMfgMatched: 0,
  exactMatch: 0,
  possibleMatch: 0,
  closeMatch: 0,
  removedFromQuote: 0,
  privateLabelMatched: 0,
  privateLabelSwapped: 0,
  markedAsNonMatched: 0,
  userSelection: 0
};

const initalState: RFQStoreStates = {
  // RFQ Details
  name: '',
  description: '',
  competitor: null,

  // Quote related details
  quoteId: null,
  customerId: null,
  locationId: null,
  segmentId: null,
  prospect: null,

  // Products & Processing
  products: [],
  processedProducts: [],
  selectedProducts: [],
  productDetails: null,

  // File Management
  files: [],
  selectedFile: null,
  currentFileId: null,
  currentJobId: null,

  // Processing State
  uploading: false,
  processing: false,
  polling: false,
  completed: false,
  isDialogOpen: false,
  isSocketActive: false,
  isSorting: false,

  // Analytics & Totals
  filterAnalytics: initAnalytics,
  analytics: initAnalytics,

  totalsToUpdate: {
    exactMatch: 0,
    possibleMatch: 0,
    closeMatch: 0,
    privateLabelSwapped: 0
  },
  processingTime: '',
  privateLabelsSelected: [],

  // Configuration
  rankings: {
    enablePrivateLabelRanking: true,
    enableStockProductRanking: true,
    enableVendorRanking: true
  },
  params: {
    enablePostProcessing: true,
    enableMatchProductBySku: true,
    enableFindProductOnline: true,
    enableGettingCompetitorProductDescription: false
  }
};

export const useRFQStore = create<Store>(set => ({
  ...initalState,

  // Core Updates
  updateCompetitor: (newCompetitor: CompetitorInterface) =>
    set({ competitor: newCompetitor }),
  updateAdditionalParams: (newParams: AdditionalParams) =>
    set({ params: newParams }),
  updateCompleted: (newCompleted: boolean) => set({ completed: newCompleted }),
  updateAnalytics: (newAnalytics: AnalyticsProps) =>
    set(state => ({ analytics: { ...state?.analytics, ...newAnalytics } })),
  updateAnalytic: (newAnalytics: Partial<AnalyticsProps>) =>
    set(state => ({ analytics: { ...state.analytics, ...newAnalytics } })),
  updateDescription: (newDescription: string) =>
    set({ description: newDescription }),
  updateProducts: (newProducts: RFQProducts[]) =>
    set({ products: newProducts }),
  setProducts: (newProducts: RFQProducts[]) => set({ products: newProducts }),
  addProducts: (newProducts: RFQProducts[]) =>
    set(state => ({ products: [...state.products, ...newProducts] })),
  appendProcessedProducts: (newProcessedProducts: RFQProducts) =>
    set(state => ({
      products: [...state.products, newProcessedProducts],
      processedProducts: [...state.processedProducts, newProcessedProducts]
    })),
  updateProcessedProducts: (newProducts: RFQProducts[]) =>
    set({
      processedProducts: newProducts,
      products: newProducts // Also update products for backward compatibility
    }),
  updateProcessedProduct: (id: string, updatedProduct: Partial<RFQProducts>) =>
    set(state => {
      const updatedProcessedProducts = state.processedProducts.map(product =>
        product.id === id ? { ...product, ...updatedProduct } : product
      );
      return {
        processedProducts: updatedProcessedProducts,
        products: updatedProcessedProducts // Also update products for backward compatibility
      };
    }),
  updateSelectedProducts: (products: Product[]) =>
    set({ selectedProducts: products }),
  updateProductDetails: (product: Product | null) =>
    set({ productDetails: product }),
  updateProcessing: (newProcessing: boolean) =>
    set({ processing: newProcessing }),
  updatePolling: (newPolling: boolean) => set({ polling: newPolling }),
  updateName: (newName: string) => set({ name: newName }),
  updateRankings: (newRankings: RankingValues) =>
    set({ rankings: newRankings }),
  updateTotalsToUpdate: (totals: Partial<ProcessingTotals>) =>
    set(state => ({ totalsToUpdate: { ...state.totalsToUpdate, ...totals } })),
  updateProcessingTime: (time: string) => set({ processingTime: time }),
  updatePrivateLabelsSelected: (ids: string[]) =>
    set(state => ({
      privateLabelsSelected: [...state.privateLabelsSelected, ...ids]
    })),
  updateSorting: (newSorting: boolean) => set({ isSorting: newSorting }),

  updateQuoteId: (quoteId: string) => set({ quoteId }),
  updateCustomerId: (customerId: string) => set({ customerId }),
  updateLocationId: (locationId: string) => set({ locationId }),
  updateSegmentId: (segmentId: string) => set({ segmentId }),
  updateProspect: (prospect: {
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
    name?: string;
  }) => set({ prospect }),

  // File Management
  updateFileUrls: (files: FileInfo[]) => {
    set({ files });
  },
  updateFile: (id: string, file: Partial<FileInfo>) =>
    set(state => {
      const updatedFiles = state.files.map(f =>
        f.id === id ? { ...f, ...file } : f
      );

      return {
        files: updatedFiles
      };
    }),
  addFileUrl: (file: FileInfo) => {
    set(state => ({ files: [...state.files, file] }));
  },
  removeFileUrl: (index: number) =>
    set(state => ({
      files: state.files.filter((_, i) => i !== index)
    })),
  updateSelectedFile: (fileName: string | null) =>
    set({ selectedFile: fileName }),

  // Dialog State
  updateDialogOpen: (isOpen: boolean) => set({ isDialogOpen: isOpen }),

  // Batch Update
  batchUpdate: (updates: Partial<RFQStoreStates>) => {
    set(updates);
  },

  updateFilterAnalytics: (analytics: Partial<AnalyticsProps> | undefined) =>
    set(state => ({
      filterAnalytics: {
        ...state?.filterAnalytics,
        ...analytics
      } as AnalyticsProps
    })),

  reset: () => set({ ...initalState })
}));
